import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "April 1, 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0527] via-[#2D063E] to-[#1a0527]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1a0527]/90 backdrop-blur-sm border-b border-[#4A0A66]/30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#FDF2F8]/70 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          <div className="flex items-center gap-2 text-[#0FFFE2]">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-semibold">Privacy Policy</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Privacy Policy</h1>
          <p className="text-[#FDF2F8]/60">Last Updated: {lastUpdated}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introduction */}
          <section className="bg-[#2D063E]/30 border border-[#4A0A66]/30 rounded-xl p-6">
            <p className="text-[#FDF2F8]/80 leading-relaxed">
              UnCloned® Media ("we," "us," or "our") operates the UnCloned® Journal application. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our application. Please read this privacy policy carefully. By using the app, 
              you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-[#0FFFE2] mb-3">Personal Information</h3>
            <p className="text-[#FDF2F8]/80 mb-4">When you create an account, we collect:</p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Email address</li>
              <li>Name (first and last)</li>
              <li>Profile picture (if using Google Sign-In)</li>
              <li>Phone number (optional)</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#0FFFE2] mt-6 mb-3">Journal Content</h3>
            <p className="text-[#FDF2F8]/80 mb-4">When you use our app, we store:</p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Journal entries and text content</li>
              <li>Mood selections and tracking data</li>
              <li>Voice recordings (temporarily processed for transcription)</li>
              <li>Photos uploaded to entries</li>
              <li>AI Coach conversation history</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#0FFFE2] mt-6 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Device type and browser information</li>
              <li>IP address and approximate location</li>
              <li>App usage statistics and feature interactions</li>
              <li>Login timestamps and session data</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">2. How We Use Your Information</h2>
            <p className="text-[#FDF2F8]/80 mb-4">We use your information to:</p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li>Provide and maintain the journaling service</li>
              <li>Process voice recordings for transcription (via OpenAI Whisper)</li>
              <li>Generate AI Coach reflections and insights (via OpenAI)</li>
              <li>Send you important account notifications</li>
              <li>Send marketing emails (with your consent, you can unsubscribe anytime)</li>
              <li>Analyze usage patterns to improve our service</li>
              <li>Respond to your support requests</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">3. Third-Party Services</h2>
            <p className="text-[#FDF2F8]/80 mb-4">We use the following third-party services:</p>
            
            <div className="space-y-4">
              <div className="bg-[#4A0A66]/20 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Google Services</h4>
                <p className="text-[#FDF2F8]/70 text-sm">Authentication (Sign-In) and Analytics. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0FFFE2] hover:underline">Google Privacy Policy</a></p>
              </div>
              
              <div className="bg-[#4A0A66]/20 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Resend (Email Delivery)</h4>
                <p className="text-[#FDF2F8]/70 text-sm">Transactional and marketing emails are sent via Resend's email infrastructure. <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#0FFFE2] hover:underline">Resend Privacy Policy</a></p>
              </div>
              
              <div className="bg-[#4A0A66]/20 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">OpenAI</h4>
                <p className="text-[#FDF2F8]/70 text-sm">AI Coach reflections and voice transcription (Whisper). Your journal content is sent to OpenAI for processing but is not used to train their models. <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0FFFE2] hover:underline">OpenAI Privacy Policy</a></p>
              </div>
              
              <div className="bg-[#4A0A66]/20 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Stripe (Payment Processing)</h4>
                <p className="text-[#FDF2F8]/70 text-sm">If you subscribe to premium features, Stripe processes your payment. We do not store your credit card information. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0FFFE2] hover:underline">Stripe Privacy Policy</a></p>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">4. Data Retention</h2>
            <p className="text-[#FDF2F8]/80">
              As a journaling application, we retain your data indefinitely to preserve your journal history. 
              Your entries are yours to keep for as long as you maintain your account. If you delete your 
              account, all your data will be permanently deleted within 30 days.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">5. Data Security</h2>
            <p className="text-[#FDF2F8]/80">
              We implement industry-standard security measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4 mt-4">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
            </ul>
            <p className="text-[#FDF2F8]/80 mt-4">
              However, no method of transmission over the Internet is 100% secure. While we strive to 
              protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">6. Your Rights</h2>
            <p className="text-[#FDF2F8]/80 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-[#FDF2F8]/70 space-y-2 ml-4">
              <li><strong>Access</strong> — Request a copy of your personal data</li>
              <li><strong>Correct</strong> — Update inaccurate information in your profile</li>
              <li><strong>Delete</strong> — Request deletion of your account and all associated data</li>
              <li><strong>Export</strong> — Download your journal entries (PDF export feature)</li>
              <li><strong>Unsubscribe</strong> — Opt out of marketing emails at any time</li>
            </ul>
            <p className="text-[#FDF2F8]/80 mt-4">
              To exercise these rights, please contact us through our support desk.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">7. Children's Privacy</h2>
            <p className="text-[#FDF2F8]/80">
              Our service is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you are a parent or guardian and believe your 
              child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-xl font-bold text-[#FF7901] mb-4">8. Changes to This Policy</h2>
            <p className="text-[#FDF2F8]/80">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date. You are 
              advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Us */}
          <section className="bg-gradient-to-r from-[#df1283]/20 to-[#FF7901]/20 border border-[#df1283]/30 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4">9. Contact Us</h2>
            <p className="text-[#FDF2F8]/80 mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="space-y-2">
              <p className="text-[#FDF2F8]">
                <strong>UnCloned® Media</strong>
              </p>
              <p className="text-[#FDF2F8]/80">
                Support Desk: <a href="https://www.unclonedsupport.com" target="_blank" rel="noopener noreferrer" className="text-[#0FFFE2] hover:underline">www.unclonedsupport.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#4A0A66]/30 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#FDF2F8]/50 text-sm">
            © 2026 UnCloned® Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
